import React, { useEffect, useState } from "react";
import PartnerLayout from "@/components/PartnerLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Activity, Clock } from "lucide-react";

const ACTION_LABELS = {
  "tenant.update": "Tenant actualizado",
  "tenant.admin_added": "Admin agregado",
  "tenant.admin_created": "Admin creado",
  "tenant.admin_removed": "Admin removido",
  "partner.update": "Partner actualizado",
  "landing.publish": "Landing publicada",
  "landing.update": "Landing editada",
  "landing.blocks.update": "Bloques editados",
  "licenses.batch": "Batch de licencias",
  "licenses.csv_import": "Import CSV",
  "licenses.revoke": "Licencia revocada",
  "branding.update": "Branding actualizado",
  "user.create": "Usuario creado",
  "user.update": "Usuario actualizado",
};

const ACTION_COLORS = {
  "tenant.": "text-brand-orange-500",
  "partner.": "text-blue-400",
  "landing.": "text-emerald-400",
  "licenses.": "text-purple-400",
  "branding.": "text-pink-400",
  "user.": "text-yellow-400",
};

function actionColor(action) {
  for (const prefix in ACTION_COLORS) {
    if (action.startsWith(prefix)) return ACTION_COLORS[prefix];
  }
  return "text-gray-400";
}

export default function PartnerAuditLog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantSlug, setTenantSlug] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { data } = await api.get("/partner/audit-log?limit=100");
        setItems(data.items || []);
        // Resolve slug from active tenant
        const mt = await api.get("/auth/my-tenants");
        const active = (mt.data.items || []).find((t) => t.id === data.tenant_id);
        setTenantSlug(active?.slug || "?");
      } catch (e) {
        console.error(e);
      } finally { setLoading(false); }
    }
    load();
  }, []);

  return (
    <PartnerLayout user={user}>
      <div>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">Fase 14.2 · Audit log</p>
          <h1 className="mt-2 text-3xl font-bold text-white font-display flex items-center gap-2">
            <Activity className="w-7 h-7" /> Actividad
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Últimos 100 eventos del tenant activo <span className="font-mono text-brand-orange-500">/t/{tenantSlug}</span>
          </p>
        </div>

        <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-hidden" data-testid="partner-audit-log-table">
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              Sin eventos registrados aún. Los cambios que hagas en branding, licencias, admins, etc. aparecerán aquí.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#0B0F14] border-b border-gray-800">
                <tr>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Fecha</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Acción</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Actor</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Detalle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {items.map((i) => (
                  <tr key={i.id} className="hover:bg-[#0B0F14]" data-testid={`audit-row-${i.id}`}>
                    <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {(i.created_at || "").slice(0, 19).replace("T", " ")}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`text-sm font-semibold ${actionColor(i.action)}`}>
                        {ACTION_LABELS[i.action] || i.action}
                      </div>
                      <div className="text-[10px] text-gray-500 font-mono">{i.action}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="text-white">{i.actor_name || "—"}</div>
                      <div className="text-gray-500 font-mono">{i.actor_email || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {Object.keys(i.metadata || {}).length > 0 ? (
                        <details className="cursor-pointer">
                          <summary className="text-brand-orange-500 hover:underline">Ver</summary>
                          <pre className="mt-2 p-2 bg-[#0B0F14] border border-gray-800 rounded text-[10px] font-mono text-gray-300 max-w-md overflow-x-auto">
                            {JSON.stringify(i.metadata, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PartnerLayout>
  );
}
